import { Component, OnInit, Inject, Optional } from '@angular/core';
import { MdDialogRef, MD_DIALOG_DATA } from '@angular/material';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FinanceService } from 'app/finance/finance.service';
import { BankStatement } from '../../finance/bank-statement.model';
import { ObservableRequestService } from 'app/services/observable-request.service';

@Component({
  selector: 'app-bank-statement-preview',
  templateUrl: './bank-statement-preview.component.html',
  styleUrls: ['./bank-statement-preview.component.css']
})
export class BankStatementPreviewComponent implements OnInit {
  bankStatement: BankStatement;
  fileUrl: SafeResourceUrl;
  isLoading = true;
  errorMessage: string;
  fileType: string;
  officeViewerUrl: SafeResourceUrl;

  constructor(
    public dialogRef: MdDialogRef<BankStatementPreviewComponent>,
    private financeService: FinanceService,
    private sanitizer: DomSanitizer,
    private requestService: ObservableRequestService,
    @Optional() @Inject(MD_DIALOG_DATA) private dialogData: any
  ) { }

  ngOnInit() {
    // Check if bankStatement was set directly (from entity-finance component)
    if (this.bankStatement && this.bankStatement.uniqueDocumentName) {
      this.loadFile();
    } 
    // Check if data was passed via dialog config (from batch component)
    else if (this.dialogData && this.dialogData.bankStatementId) {
      this.loadBankStatementFromId(this.dialogData.bankStatementId);
    } else {
      this.errorMessage = 'Bank statement information is missing';
      this.isLoading = false;
    }
  }

  loadBankStatementFromId(bankStatementId: number) {
    const url = `/bank-statement/${bankStatementId}`;
    this.requestService.getRequest(url).subscribe(
      (bankStatementDetails: any) => {
        // Construct BankStatement object from the response
        this.bankStatement = {
          id: bankStatementDetails.id,
          uniqueDocumentName: bankStatementDetails.uniqueNameId,
          documentName: bankStatementDetails.originalName,
          tenantId: bankStatementDetails.tenantId,
          bankAccount: bankStatementDetails.bankAccount ? {
            accountId: bankStatementDetails.bankAccount.accountId,
            accountName: bankStatementDetails.bankAccount.accountName,
            accountNumber: bankStatementDetails.bankAccount.accountNumber
          } : null,
          jsonData: bankStatementDetails.jsonData ? JSON.parse(bankStatementDetails.jsonData) : null
        };
        this.loadFile();
      },
      (error: any) => {
        this.errorMessage = 'Error loading bank statement: ' + (error.message || 'Unknown error');
        this.isLoading = false;
      }
    );
  }

  loadFile() {
    if (!this.bankStatement.uniqueDocumentName || !this.bankStatement.tenantId) {
      this.errorMessage = 'Missing information to preview bank statement';
      this.isLoading = false;
      return;
    }

    // For batch statements, bankAccountId can be null/0
    const bankAccountId = this.bankStatement.bankAccount ? this.bankStatement.bankAccount.accountId : 0;

    this.financeService.downloadBankStatement(
      this.bankStatement.uniqueDocumentName,
      bankAccountId,
      this.bankStatement.tenantId,
      this.bankStatement.documentName
    ).subscribe(
      (blob: Blob) => {
        // Determine file type
        const fileName = this.bankStatement.documentName || this.bankStatement.uniqueDocumentName;
        const lastDotIndex = fileName.lastIndexOf('.');
        const extension = lastDotIndex >= 0 && lastDotIndex < fileName.length - 1
          ? fileName.substring(lastDotIndex + 1).toLowerCase()
          : '';
        
        // Office file extensions
        const officeExtensions = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'odt', 'ods', 'odp'];
        const isOfficeFile = officeExtensions.indexOf(extension) !== -1;
        
        // Check if it's a PDF
        if (extension === 'pdf') {
          this.fileType = 'pdf';
          const url = window.URL.createObjectURL(blob);
          this.fileUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
          this.isLoading = false;
        } else if (isOfficeFile) {
          // For Office files, use Google Docs Viewer or Microsoft Office Online Viewer
          this.fileType = 'office';
          // Build the download URL for the file
          const bankAccountId = this.bankStatement.bankAccount ? this.bankStatement.bankAccount.accountId : 0;
          const downloadUrl = `/bank-statement/download?uniqueDocumentName=${encodeURIComponent(this.bankStatement.uniqueDocumentName)}&bankAccountId=${bankAccountId}&tenantId=${this.bankStatement.tenantId}&documentName=${encodeURIComponent(this.bankStatement.documentName || this.bankStatement.uniqueDocumentName)}`;
          
          // Use Google Docs Viewer (works better with authenticated URLs)
          // Format: https://docs.google.com/viewer?url=FILE_URL&embedded=true
          const fullUrl = window.location.origin + downloadUrl;
          const officeViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(fullUrl)}&embedded=true`;
          this.officeViewerUrl = this.sanitizer.bypassSecurityTrustResourceUrl(officeViewerUrl);
          this.isLoading = false;
        } else {
          // For all other files (including MT940 with various extensions or no extension),
          // try to read as text. MT940 files are plain text files.
          // Common MT940 extensions: .mt940, .mt, .940, .fin, .txt, .csv, or no extension
          this.fileType = 'text';
          const reader = new FileReader();
          reader.onload = (e) => {
            this.fileContent = (e.target as FileReader).result as string;
            this.isLoading = false;
          };
          reader.onerror = (e) => {
            this.errorMessage = 'Error reading file content';
            this.isLoading = false;
          };
          reader.readAsText(blob);
        }
      },
      error => {
        this.errorMessage = 'Error loading bank statement: ' + (error.message || 'Unknown error');
        this.isLoading = false;
      }
    );
  }

  fileContent: string;

  close() {
    this.dialogRef.close();
  }

  download() {
    if (!this.bankStatement.uniqueDocumentName || !this.bankStatement.tenantId) {
      alert('Missing information to download bank statement');
      return;
    }
    // For batch statements, bankAccountId can be null/0
    const bankAccountId = this.bankStatement.bankAccount ? this.bankStatement.bankAccount.accountId : 0;
    this.financeService.downloadBankStatement(
      this.bankStatement.uniqueDocumentName,
      bankAccountId,
      this.bankStatement.tenantId,
      this.bankStatement.documentName
    ).subscribe(
      (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = this.bankStatement.documentName || this.bankStatement.uniqueDocumentName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      },
      error => {
        alert('Error downloading bank statement: ' + (error.message || 'Unknown error'));
      }
    );
  }
}




// WEBPACK FOOTER //
// ./src/app/entities/transaction-monitoring/bank-statement-preview/bank-statement-preview.component.ts